import React, { FC } from 'react'

import Link from 'next/link'
import { css } from '@emotion/core'
import { useTheme } from 'emotion-theming'
import times from 'lodash/times'

import { Term } from 'components/utils/KampuspediaProvider'
import NewTypography from 'components/common/atoms/NewTypography'
import Loader from 'components/common/atoms/Loader'
import Ellipsis from 'components/common/atoms/Ellipsis'

type IndexListProps = {
  list: Term[] | undefined
  url?: string
  loading: boolean
}

const indexListStyle = (theme: ThemeType) => css`
  .index-list {
    &__item {
      display: flex;
      align-items: center;
      padding: 9.5px 0;
      border-bottom: 1px solid ${theme.gray.gray20};
      &:last-of-type {
        border-bottom: none;
      }
    }
    &__item-skeleton {
      margin-bottom: 11px;
    }
    &__item-pc {
      display: none;
    }
    @media screen and (min-width: 768px) {
      &__flex {
        display: flex;
        flex-wrap: wrap;
        row-gap: 16px;
        column-gap: 40px;
      }
      &__item {
        width: 31%;
        border-bottom: none;
        padding: 0;
        > a:hover div {
          cursor: pointer;
          font-weight: 600;
          color: ${theme.campus.aqua30};
        }
      }
      &__item-skeleton {
        margin-bottom: 0;
      }
      &__item-pc {
        display: block;
      }
      &__item-sp {
        display: none;
      }
    }
  }
`

const IndexList: FC<IndexListProps> = ({ list, url = '', loading }) => {
  const theme: ThemeType = useTheme()
  return (
    <div className="index-list" css={indexListStyle(theme)}>
      <div className="index-list__flex">
        {loading
          ? times(30, (i) => (
              <div className="index-list__item-skeleton" key={`term-skeleton-${i}`}>
                <Loader
                  dataTestId={`term-skeleton-${i}`}
                  customSize={{
                    sp: {
                      width: 276,
                      height: 15,
                      borderRadius: 10,
                    },
                    pc: {
                      width: 370,
                      height: 16,
                      borderRadius: 10,
                    },
                  }}
                />
              </div>
            ))
          : list?.map((item, idx) => (
              <div className="index-list__item" key={`term-${idx}`}>
                <Link href={`${url}${item.slug}`}>
                  <NewTypography tagName="div" variant="paragraph" level="2" className="index-list__item-sp">
                    {item.title}
                  </NewTypography>
                  <NewTypography tagName="div" variant="paragraph" level="1" className="index-list__item-pc">
                    <Ellipsis text={item.title} clamp={1} />
                  </NewTypography>
                </Link>
              </div>
            ))}
      </div>
    </div>
  )
}

export default IndexList
